
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const CountrySwitcherModal = () => import('@/components/buybox/country-switcher/CountrySwitcherModal.vue');
const BuyBoxFilter = () => import('@/components/buybox/BuyBoxFilter.vue');

import BasicButton from '@/ui-components/BasicButton.vue';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';

import type { WebLocale } from '@/enums/web-locale';
import { BuyboxOffer, BuyboxOfferCategory, BuyboxOffers } from '@/interfaces/buybox';
import Provider from '@/interfaces/provider';
import { Offer, OfferPresentationType, Season, Title, TitleObjectType } from '@/interfaces/titles';

import { ModalHelper } from '@/helpers/modal-helper';
import { BuyboxParam, OfferLocation } from '@/helpers/offer-helper';
import {
	getBuyboxCategoryByMonetization,
	getBuyboxOffers,
	hasStreamingOffers,
	trackClickOut,
} from '@/helpers/rest-helpers/offer-helper-rest';
import { SponsoredClickoutsHelper } from '@/helpers/sponsored-clickouts-helper';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { OfferMonetizationType } from '@/interfaces/titles';

const Constant = namespace('constant');
const Language = namespace('language');
const User = namespace('user');

@Component({
	name: 'BuyBoxREST',
	components: {
		BasicButton,
		BuyBoxFilter,
		ProviderIcon,
	},
})
export default class BuyBoxREST extends Vue {
	@Prop({ default: () => [], type: Array }) offers: Offer[];
	@Prop() promotedOffers!: { offers: Offer[]; categories: BuyboxOfferCategory[] };
	@Prop() title: Title;
	@Prop() season: Season;
	@Prop() objectType: TitleObjectType; // base title objectType
	@Prop() objectId: number; // base title id
	@Prop() currentObjectType: TitleObjectType; // depending on this the element_count will change (5 seasons vs 5 episodes)
	@Prop({ default: () => null }) seasonNumber: number | null;
	@Prop({ default: () => null }) episodeNumber: number | null;
	// Show filter bar
	@Prop({ default: false, type: Boolean }) showFilter!: boolean;
	// Show Physical Offers only
	@Prop(Boolean) physical: boolean;
	// Show bundle offers
	@Prop(Boolean) bundle: boolean;
	// display as inline
	@Prop(Boolean) inline: boolean;
	// whether data is still fetching and blocking elements need to be displayed
	@Prop({ default: false }) isFetching: boolean;
	// whether the buybox is promoted
	@Prop({ default: false }) promoted: boolean;
	// which category the buybox is promoted
	@Prop({ default: false }) promotedCategories: BuyboxOfferCategory[];
	// no header 'WATCH NOW'
	@Prop({ default: false }) noHeader: boolean;
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];
	@Prop({ default: false }) isMiddleBuyBox: boolean;

	@Constant.Getter providersById: Record<string, Provider>;

	@Language.Getter language: string;
	@Language.Getter country: string;
	@Language.State webLocale: WebLocale;

	@User.Action setBuyboxPresentationType: (payload: { presentationType: OfferPresentationType | null }) => void;
	@User.Getter buyboxPresentationType: OfferPresentationType;

	// offers get created with a domainSessionId in their link out url.
	// this is needed since domainSessionId is not available beforehand, but still the link needs to be created with contexts.
	// changing domainSessionId later will also trigger currentOffers anew.
	protected domainSessionId = '' as string | undefined;

	get categoryTranslatables() {
		return {
			[BuyboxOfferCategory.STREAM]: this.$t('WEBAPP_STREAM'),
			[BuyboxOfferCategory.RENT]: this.$t('WEBAPP_RENT'),
			[BuyboxOfferCategory.BUY]: this.$t('WEBAPP_BUY'),
			[BuyboxOfferCategory.BUNDLE]: this.$t('WEBAPP_BUNDLE'),
		};
	}

	get currentOffers(): BuyboxOffers {
		let buyboxCategories: BuyboxOfferCategory[] = [];

		if (this.physical) {
			buyboxCategories = [BuyboxOfferCategory.PHYSICAL];
		} else if (this.promoted) {
			buyboxCategories = this.promotedCategories;
		} else {
			buyboxCategories = [BuyboxOfferCategory.STREAM, BuyboxOfferCategory.RENT, BuyboxOfferCategory.BUY];
		}

		const offers =
			getBuyboxOffers(
				this.offers,
				buyboxCategories,
				this.language,
				this.country,
				this.currentObjectType,
				this.providersById,
				// ignore buybox presentation type filter when promoted or no filter bar present
				this.promoted || !this.showFilter ? undefined : this.buyboxPresentationType,
				this.domainSessionId,
				// needed for promoted buybox
				this.promoted ? BuyboxParam.Promoted : BuyboxParam.Normal
			) || [];
		return offers;
	}

	// BUYBOX_BUNDLE: Prioritize showing bundle provider icon over package icon
	getProviderIconUrl(offer: BuyboxOffer): string {
		if (offer.offer.monetization_type === OfferMonetizationType.BUNDLE) {
			return offer.offer.bundle_icon_url || '';
		}
		return '';
	}

	// some pages show more than one title (watchlist, search)
	// so in the TitleDetailsStore title === undefined && offers === undefined
	get hasAnyLocalOffers() {
		return hasStreamingOffers(this.offers);
	}

	get presentationTypes() {
		return {
			[OfferPresentationType.HD]: {
				text: 'HD',
				name: 'hd',
			},
			[OfferPresentationType._4K]: {
				text: '4K',
				name: 'hd',
			},
			[OfferPresentationType.DVD]: {
				text: 'DVD',
				name: 'dvd',
			},
			[OfferPresentationType.BLURAY]: {
				text: 'Blu-Ray',
				name: 'bluray',
			},
		};
	}

	get promotedContainerClass() {
		return {
			'price-comparison__promoted__container clearfix': this.promoted,
		};
	}

	get categories(): BuyboxOfferCategory[] {
		if (this.promoted) {
			return this.promotedCategories;
		} else if (this.physical) {
			return [BuyboxOfferCategory.PHYSICAL];
		} else if (this.buyboxPresentationType === OfferPresentationType.FREE) {
			return [BuyboxOfferCategory.STREAM];
		} else {
			const categoryOrderKeys = Object.keys(this.categoryTranslatables);
			const updatedOffers = this.offers
				.map(offer => getBuyboxCategoryByMonetization(offer.monetization_type))
				.filter(key => key !== undefined)
				.filter((item, index, offers) => {
					return offers.indexOf(item) === index;
				}) as BuyboxOfferCategory[];

			// Even if title has no offers, Stream category should be in buy box - to be able to show "Notify Me Bell"
			if (!updatedOffers.length) {
				updatedOffers.push(BuyboxOfferCategory.STREAM);
			}

			updatedOffers.sort(
				(a, b) => categoryOrderKeys.indexOf(a as string) - categoryOrderKeys.indexOf(b as string)
			);

			return updatedOffers;
		}
	}

	get noFreeOfferCTA() {
		return this.$t('WEBAPP_WATCHLIST_FREE_CTA');
	}

	get titleHasPromotedBuyBox() {
		return this.promotedOffers?.offers?.length > 0;
	}

	isSponsoredOffer(offer: BuyboxOffer): boolean {
		return this.promoted || SponsoredClickoutsHelper.isSponsoredOffer(offer, this.webLocale);
	}

	shouldLazyLoad(categoryIndex: number, index: number): boolean {
		switch (this.currentObjectType) {
			case TitleObjectType.MOVIE:
			case TitleObjectType.SEASON:
				const isVisibleHorizontally = this.promoted || !this.titleHasPromotedBuyBox || categoryIndex < 1;
				const isVisibleVertically = index < global.IMG_PROVIDERS_BUY_BOX_COUNT;

				return !isVisibleHorizontally || !isVisibleVertically;
			case TitleObjectType.SHOW:
			default:
				return true;
		}
	}

	buyboxPresentationTypeChangeHandler(presentationType: OfferPresentationType | null) {
		this.setBuyboxPresentationType({ presentationType });
	}

	translateOffer(offer: BuyboxOffer) {
		if (
			offer.translatable &&
			offer.translatable.name &&
			offer.offer.monetization_type === OfferMonetizationType.FLATRATE_AND_BUY
		) {
			return `${this.$t(offer.translatable.name, offer.translatable.properties)} + ${offer.price}`;
		} else if (offer.translatable && offer.translatable.name) {
			return this.$t(offer.translatable.name, offer.translatable.properties);
		}
		return offer.price;
	}

	clickOut(offer: BuyboxOffer) {
		let additionalContexts = this.additionalContexts;

		// We want to change the monetization_type here to 'flat', Since we do not have an 'official' bundle monetization type,
		// and we only added bundle to the OfferMonetizationType to show the bundles correctly on the buybox.
		if (offer.offer.monetization_type === OfferMonetizationType.BUNDLE) {
			offer.offer.monetization_type = OfferMonetizationType.FLATRATE;

			// Add packages tracking if the monetization_type is bundle
			additionalContexts.push(
				TrackingHelper.getPackageContext(
					offer.provider.packageId,
					offer.provider.technicalName,
					OfferMonetizationType.BUNDLE,
					offer.offer.bundle_id as number,
					offer.offer.bundle_technical_name as string
				)
			);
		}

		let offerLocation = OfferLocation.REGULAR_BUYBOX;
		if (this.promoted) {
			offerLocation = OfferLocation.PROMOTED_BUYBOX;
		} else if (this.isMiddleBuyBox) {
			offerLocation = OfferLocation.MIDDLE_BUYBOX;
		}

		trackClickOut(
			offer.offer,
			offer.provider,
			this.currentObjectType,
			this.title.id,
			this.seasonNumber,
			this.episodeNumber,
			offerLocation,
			additionalContexts
		);
	}

	openCountrySwitcherModal() {
		ModalHelper.openModal(CountrySwitcherModal, { closable: true });
		TrackingHelper.trackEvent('userinteraction', {
			action: 'country_selector_clicked',
		});
	}
}
